import { z } from 'zod';
import { Role } from './auth.js';
import { isoDate } from './common.js';

/**
 * Вопросы бригадиру (specs/003 contracts/http-api.md, data-model.md). Вопрос выводится при чтении из пометок дня
 * и проверки «две бригады» (research R1); ключ — табель, человек, дата, тип. Тип — код пометки (`A1.нет_выхода` …)
 * или `сервис.две_бригады`: какие коды — вопросы, задаёт параметр E0a `вопрос_бригадиру` правил объекта.
 */

/** Состояние вопроса. Без ответа — `open`, `later`, `clarify`; `removed` в выводе не бывает. */
export const QUESTION_STATES = ['open', 'later', 'answered', 'accepted', 'clarify', 'removed'] as const;
export const QuestionState = z.enum(QUESTION_STATES);
export type QuestionState = z.infer<typeof QuestionState>;

/** Состояния «без ответа»: их считают счётчики и правило сдачи. */
export const UNANSWERED_STATES: readonly QuestionState[] = ['open', 'later', 'clarify'];

/**
 * Вариант ответа: `time` — время бригадира (Q-27, «выход в 19:00»), `hours` — указать часы, `zero` — работы не было,
 * `accept` — расчёт верен («да, верно», «весь день в моей бригаде»), `later` — «Уточню позже».
 */
export const ANSWER_OPTIONS = ['time', 'hours', 'zero', 'accept', 'later'] as const;
export const AnswerOption = z.enum(ANSWER_OPTIONS);
export type AnswerOption = z.infer<typeof AnswerOption>;

export const StatedKind = z.enum(['вход', 'выход']);
export type StatedKind = z.infer<typeof StatedKind>;

/** Факты дня для текста вопроса: проходы, что в ячейке, часы другой бригады (у «две бригады», без ФИО бригадира). */
export const QuestionFacts = z.object({
  passes: z.array(
    z.object({
      /** Местное время объекта `HH:MM`. */
      time: z.string(),
      direction: z.enum(['IN', 'OUT']).nullable(),
      terminal: z.string(),
      /** Проход следующей даты (ночная смена). */
      nextDay: z.boolean().optional(),
    }),
  ),
  /** Машинные часы; нет — `null`. */
  machineHours: z.number().nullable(),
  /** Машинные часы в формате R5 правил объекта. */
  machineValue: z.string().nullable().optional(),
  /** Что сейчас стоит в ячейке: часы, правка, состояние («нет выхода»), пусто. */
  cell: z.object({
    kind: z.enum(['hours', 'edit', 'state', 'empty']),
    value: z.string().nullable(),
  }),
  /** «Две бригады»: другая бригада и её часы за эту дату. */
  otherBrigade: z
    .object({ name: z.string(), hours: z.number().nullable(), value: z.string().nullable().optional() })
    .optional(),
});
export type QuestionFacts = z.infer<typeof QuestionFacts>;

/** Вариант ответа на вопрос этого типа: подпись и подсказка простыми словами (тексты — у сервера). */
export const QuestionOptionItem = z.object({
  key: AnswerOption,
  label: z.string(),
  hint: z.string().optional(),
});
export type QuestionOptionItem = z.infer<typeof QuestionOptionItem>;

export const QuestionAnswer = z.object({
  option: AnswerOption,
  statedKind: StatedKind.optional(),
  /** Время бригадира, ISO; выход следующего дня — с датой D+1. */
  statedAt: z.string().optional(),
  hours: z.number().nullable().optional(),
  /** Пояснение бригадира. */
  note: z.string().nullable().optional(),
  /** ФИО автора ответа; без пользователя — `null`. */
  by: z.string().nullable(),
  byRole: Role.optional(),
  at: z.string(),
  changedAfterAccept: z.boolean(),
  /** Ответ правкой часов через «Изменить часы» (data-model, «Правка как ответ»). */
  via: z.enum(['answer', 'edit']).optional(),
});
export type QuestionAnswer = z.infer<typeof QuestionAnswer>;

export const ReviewDecision = z.enum(['accepted', 'clarify']);
export type ReviewDecision = z.infer<typeof ReviewDecision>;

export const QuestionReview = z.object({
  decision: ReviewDecision,
  comment: z.string().nullable().optional(),
  by: z.string().nullable(),
  at: z.string(),
});
export type QuestionReview = z.infer<typeof QuestionReview>;

export const Question = z.object({
  /** `question_states.id`: строка создаётся при первом показе вопроса. */
  id: z.number().int(),
  personId: z.string(),
  displayName: z.string(),
  date: isoDate,
  type: z.string(),
  state: QuestionState,
  /** Появился вчера по времени объекта (время появления — из источника, data-model `first_seen_at`). */
  isNew: z.boolean(),
  /** Дней без ответа от появления; с ответом — `null`. */
  daysWithoutAnswer: z.number().int().nullable(),
  /** Без ответа дольше порога E0a `давно_без_ответа_дней`. */
  old: z.boolean(),
  facts: QuestionFacts,
  options: z.array(QuestionOptionItem),
  answer: QuestionAnswer.optional(),
  review: QuestionReview.optional(),
  /**
   * Вопрос снят системой (`state: 'removed'`; только в `CalcResponse.removedQuestions`, 003 T109): почему —
   * `flag_gone` (пометка исчезла после пересчёта), `absence` (документ дня), `row_removed` (человека убрали из табеля),
   * `not_eligible` (день больше не подходит для вопроса).
   */
  removedReason: z.enum(['flag_gone', 'absence', 'row_removed', 'not_eligible']).optional(),
  /** Когда снят, ISO. */
  removedAt: z.string().optional(),
  /** Оптимистическая версия строки: ответ и решение кадров передают её, устаревшая — `409 STALE_STATE`. */
  version: z.number().int(),
});
export type Question = z.infer<typeof Question>;

/** Правило сдачи табеля с вопросами без ответа (Q5 `завершение_с_вопросами`). */
export const CompleteWithOpen = z.enum(['разрешить_с_подтверждением', 'запретить']);
export type CompleteWithOpen = z.infer<typeof CompleteWithOpen>;

export const QuestionsQuery = z.object({
  state: z.enum(['open', 'done', 'all']).optional().default('all'),
  date: isoDate.optional(),
  personId: z.string().optional(),
});
export type QuestionsQuery = z.infer<typeof QuestionsQuery>;

export const QuestionsResponse = z.object({
  questions: z.array(Question),
  counts: z.object({
    open: z.number().int(),
    later: z.number().int(),
    clarify: z.number().int(),
    answered: z.number().int(),
    accepted: z.number().int(),
    old: z.number().int(),
    newYesterday: z.number().int(),
  }),
  params: z.object({
    /** E0a `порог_группы`: с какого числа вопросов одного типа за дату предлагать «Ответить всем сразу». */
    groupThreshold: z.number().int(),
    /** E0a `давно_без_ответа_дней`. */
    oldAfterDays: z.number().int(),
    completeWithOpen: CompleteWithOpen,
  }),
});
export type QuestionsResponse = z.infer<typeof QuestionsResponse>;

/** Ответ бригадира: вариант и, если вариант требует, время (`HH:MM`) или часы; пояснение — до 300 символов. */
export const AnswerInput = z.object({
  option: AnswerOption,
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  hours: z.number().min(0).max(24).optional(),
  note: z.string().trim().max(300).optional(),
});
export type AnswerInput = z.infer<typeof AnswerInput>;

/** Вопросы одного типа (1–50) с версиями и один ответ: предпросмотр и запись. */
export const AnswerRequest = z.object({
  items: z
    .array(z.object({ questionId: z.number().int().positive(), version: z.number().int().positive() }))
    .min(1)
    .max(50),
  answer: AnswerInput,
});
export type AnswerRequest = z.infer<typeof AnswerRequest>;
export const PreviewRequest = AnswerRequest;
export type PreviewRequest = AnswerRequest;

/** Итог предпросмотра по вопросу: часы и строки расчёта, комментарий правки, предупреждения, ошибка ввода. */
export const PreviewResult = z.object({
  questionId: z.number().int(),
  ok: z.boolean(),
  hours: z.number().nullable().optional(),
  value: z.string().nullable().optional(),
  lines: z.array(z.string()).optional(),
  /** Коды предупреждений: `SHIFT_OVER_16H`, `DAY_OVER_16H`, `TWO_BRIGADES_SUM` (не блокируют). */
  warnings: z.array(z.string()).optional(),
  /** Ошибка ввода (`TIME_INVALID`, `EXIT_BEFORE_ENTRY`, `HOURS_INVALID`): сохранить нельзя. */
  error: z.object({ code: z.string(), message: z.string() }).optional(),
  /** Отрезок по ответу временем — для полосы дня: местное время `HH:MM`. */
  span: z.object({ from: z.string(), to: z.string(), nextDay: z.boolean().optional() }).optional(),
  /** Комментарий правки, который будет записан. */
  comment: z.string().optional(),
});
export type PreviewResult = z.infer<typeof PreviewResult>;

export const PreviewResponse = z.object({ results: z.array(PreviewResult) });
export type PreviewResponse = z.infer<typeof PreviewResponse>;

/** Записанный ответ: вопросы после записи и действие для «Отменить» (US3). Табель интерфейс перечитывает сам. */
export const AnswerResponse = z.object({
  questions: z.array(Question),
  undoActionId: z.number().int().nullable(),
});
export type AnswerResponse = z.infer<typeof AnswerResponse>;

/** Решение кадров: при «Уточнить» комментарий обязателен (`400 COMMENT_REQUIRED`). */
export const ReviewRequest = z.object({
  decision: ReviewDecision,
  comment: z.string().trim().max(500).optional(),
  version: z.number().int().positive(),
});
export type ReviewRequest = z.infer<typeof ReviewRequest>;

export const ReviewResponse = z.object({ question: Question });
export type ReviewResponse = z.infer<typeof ReviewResponse>;

/** Сводка табеля перед «Завершить» (R10). */
export const TimesheetSummary = z.object({
  people: z.number().int(),
  hours: z.number(),
  hoursValue: z.string().nullable().optional(),
  edits: z.number().int(),
  openQuestions: z.object({ count: z.number().int(), first: z.array(Question).max(5) }),
  onSiteToday: z.number().int(),
  completeWithOpen: CompleteWithOpen,
});
export type TimesheetSummary = z.infer<typeof TimesheetSummary>;

/** Сводка при входе (R12): по табелям бригадира в «Черновик» и «Возвращён», без ФИО. */
export const QuestionsSummary = z.object({
  timesheets: z.array(
    z.object({
      id: z.number().int(),
      brigadeName: z.string(),
      periodStart: isoDate,
      periodEnd: isoDate,
      status: z.enum(['draft', 'returned']),
      newYesterday: z.number().int(),
      old: z.number().int(),
      open: z.number().int(),
    }),
  ),
});
export type QuestionsSummary = z.infer<typeof QuestionsSummary>;
